import {
  Button,
  Divider,
  Icon,
  ICONS_ENUM,
  Tooltip,
  Typography
} from '@sanlam-glacier/app-common/components';
import styles from './section-14-cards.module.scss';
import clsx from 'clsx';
import { StatusPill } from '../../atoms/status-pill/StatusPill';
import { InformationIndicator } from '../../atoms/information-indicator/InformationIndicator';
import { Section14UrgencyTab } from 'modules/my-practice/types/section-14/section-14.enums';
import { useState } from 'react';
import { Section14ClientRenewalModel } from 'bff';

type Section14CardProps = {
  row: Section14ClientRenewalModel;
  invIndex: number;
  activeTab: Section14UrgencyTab;
};

export const Section14Card = ({ row, invIndex, activeTab }: Section14CardProps) => {
  const testComponentName = 'section14Card';

  const [displayTooltip, setDisplayTooltip] = useState(false);

  const handleHoverOverCard = () => {
    setDisplayTooltip(true);
  };

  return (
    <div
      className={styles.card}
      data-testid={`${testComponentName}`}
      onMouseOver={handleHoverOverCard}
      onFocus={handleHoverOverCard}
    >
      {row.investments.length === 1 && (
        <>
          <div className={styles.cardTitle}>
            <Typography
              data-testid={`${testComponentName}ClientName`}
              variant={Typography.VARIANT.B4}
              color={Typography.COLOR.PRIMARY}
              weight={Typography.WEIGHT.MEDIUM}
            >
              {row.clientName}
            </Typography>
            <InformationIndicator
              lateRenewalsInfo={row.numLateRenewals}
              upcomingRenewalsInfo={row.numUpcomingRenewals}
              activeTab={activeTab}
            />
          </div>

          <Divider size={Divider.SIZE.SMALL} />
        </>
      )}

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Investment number`}
        </Typography>
        <Typography
          data-testid={`${testComponentName}Investment${invIndex}InvestmentNumber`}
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.NORMAL}
          color={Typography.COLOR.DEFAULT}
        >
          {row.investments[invIndex]?.investmentNumber ?? ''}
        </Typography>
      </div>

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Investment name`}
        </Typography>
        {displayTooltip ? (
          <Tooltip
            position={Tooltip.POSITION.RIGHT}
            content={row.investments[invIndex]?.investmentName ?? ''}
          >
            <span className={styles.investmentNameContainer}>
              <Typography
                data-testid={`${testComponentName}Investment${invIndex}InvestmentName`}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.NORMAL}
                color={Typography.COLOR.DEFAULT}
                className={styles.investmentName}
                align={Typography.ALIGN.RIGHT}
              >
                {row.investments[invIndex]?.investmentName ?? ''}
              </Typography>
            </span>
          </Tooltip>
        ) : (
          <span className={styles.investmentNameContainer}>
            <Typography
              data-testid={`${testComponentName}Investment${invIndex}InvestmentName`}
              variant={Typography.VARIANT.B4}
              weight={Typography.WEIGHT.NORMAL}
              color={Typography.COLOR.DEFAULT}
              className={styles.investmentName}
              align={Typography.ALIGN.RIGHT}
            >
              {row.investments[invIndex]?.investmentName ?? ''}
            </Typography>
          </span>
        )}
      </div>

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Investment value`}
        </Typography>
        <Typography
          data-testid={`${testComponentName}Investment${invIndex}InvestmentValue`}
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.NORMAL}
          color={Typography.COLOR.DEFAULT}
        >
          {row.investments[invIndex]
            ? row.investments[invIndex].investmentValue
                .toLocaleString('en-ZA', {
                  style: 'currency',
                  currency: 'ZAR',
                  minimumFractionDigits: 2
                })
                .replace(/,/g, ' ')
            : ''}
        </Typography>
      </div>

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Intermediary fee`}
        </Typography>
        {activeTab === Section14UrgencyTab.late ? (
          <Tooltip
            position={Tooltip.POSITION.TOP_END}
            content={`Previous fee: ${row.investments[invIndex]?.previousIntermediaryFee ? row.investments[invIndex].previousIntermediaryFee : row.investments[invIndex]?.intermediaryFee}%`}
          >
            <div>
              <Typography
                data-testid={`${testComponentName}Investment${invIndex}IntermediaryFee`}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.NORMAL}
                color={Typography.COLOR.PRIMARY}
              >
                {`0.00%`}
              </Typography>
            </div>
          </Tooltip>
        ) : (
          <Typography
            data-testid={`${testComponentName}Investment${invIndex}IntermediaryFee`}
            variant={Typography.VARIANT.B4}
            weight={Typography.WEIGHT.NORMAL}
            color={Typography.COLOR.PRIMARY}
          >
            {row.investments[invIndex] ? `${row.investments[invIndex].intermediaryFee}%` : ''}
          </Typography>
        )}
      </div>

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Fee renewal date`}
        </Typography>
        <Typography
          data-testid={`${testComponentName}Investment${invIndex}FeeRenewalDate`}
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.NORMAL}
          color={Typography.COLOR.DEFAULT}
        >
          {row.investments[invIndex]?.feeRenewalDate
            ? typeof row.investments[invIndex].feeRenewalDate === 'string'
              ? row.investments[invIndex].feeRenewalDate
              : (row.investments[invIndex].feeRenewalDate as Date).toLocaleDateString('en-ZA')
            : ''}
        </Typography>
      </div>

      <div className={clsx(styles.cardRow, styles.statusRow)}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Status`}
        </Typography>
        <Typography
          data-testid={`${testComponentName}Investment${invIndex}Status`}
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.NORMAL}
          color={Typography.COLOR.DEFAULT}
        >
          <StatusPill
            daysUntilDue={row.investments[invIndex] && row.investments[invIndex].daysUntilDue}
            status={row.investments[invIndex]?.renewalStatus ?? ''}
          />
        </Typography>
      </div>

      <div className={styles.cardRow}>
        <Typography
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.MEDIUM}
          color={Typography.COLOR.DEFAULT}
        >
          {`Renewal Form`}
        </Typography>
        <div
          data-testid={`${testComponentName}Investment${invIndex}RenewalFormActionButtons`}
          className={styles.renewalFormActionButtons}
        >
          <Button
            data-testid={`${testComponentName}Investment${invIndex}ViewRenewalFormButton`}
            onClick={() => {}}
            startIcon={
              <Icon icon={ICONS_ENUM.ACTION_VISIBILITY} className={styles.renewalFormIcon} />
            }
            variant={Button.VARIANT.TERTIARY}
            size={Button.SIZE.SMALL}
            disabled={true}
          />

          <Button
            data-testid={`${testComponentName}Investment${invIndex}DownloadRenewalFormButton`}
            onClick={() => {}}
            startIcon={<Icon icon={ICONS_ENUM.FILE_DOWNLOAD} className={styles.renewalFormIcon} />}
            variant={Button.VARIANT.TERTIARY}
            size={Button.SIZE.SMALL}
            disabled={true}
          />
        </div>
      </div>
    </div>
  );
};
